<template>
  <!-- Intro/Hero -->
  <section class="nylon-Intro nylon-Intro--custom" data-header-hero>
    <div class="nylon-Intro-fade"></div>
    <div class="nylon-Intro-content">
      <h2 class="nylon-Intro-heading">Welcome to <br />Cotton Bureau Custom.</h2>
      <p class="nylon-Intro-text">
        New from Cotton Bureau, now for everyone. Make (and sell) exceptional merch. We do the rest.
        <br />
        <br />
        Did we mention it’s free?
      </p>
      <div class="nylon-Intro-actions">
        <a class="nylon-Button nylon-Button--action nylon-Button--pill" href="#create-product-top" v-scroll-to="'#create-product-top'">Get Started</a>
      </div>
    </div>
  </section>

  <div id="create-product-top"></div>

  <create-product
    id="custom-create-a-product"
    :is-custom-flow="true"
    :create-from-existing-artwork="createFromExistingArtwork"
    :relaunch-campaign="relaunchCampaign"
    :initial-user="initialUser"
    :pricing-tables="pricingTables"
  ></create-product>

  <!-- How It Works -->
  <section class="reference">
    <nav class="reference-navigation">
      <!-- Needs a top value set or else it inherits the offset of the parent element. This can work as is, except it needs to fade in/out when it gets above/below the How It Works section. -->
      <ul v-scroll-spy-active v-scroll-spy-link>
        <li v-for="n in 7">
          <a :href="'#reference-pane-' + n"></a>
        </li>
      </ul>
    </nav>

    <div v-scroll-spy>
      <figure class="reference-pane" id="reference-pane-1">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <h2 class="reference-page-title">How it Works</h2>
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <p>Custom is our secret entrance into the Cotton Bureau Multiverse.</p>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      Every day the world’s most incredible designers and illustrators, causes and charities, and creative communities (podcasters, app and game developers, design agencies, blogs and
                      media outlets, etc.) turn their ideas into real products.
                    </p>
                    <p>But what about the rest of us? Shouldn’t Cotton Bureau work for everyone?</p>
                    <p>Starting today, it does. We’ve shipped over a million products since 2013, now we’re bringing the same committment to quality, service, and customziation to the masses.</p>
                    <p>What are you waiting for?</p>
                    <div class="reference-pane-actions">
                      <a class="reference-pane-action-link" href="#create-product-top" v-scroll-to="'#create-product-top'">
                        Create Your First Product
                        <NylonIcon icon-name="uiArrow--Right" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-2">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <h3 class="reference-pane-heading reference-pane-heading--full-border">Hooray! How do I get started?</h3>
                  <div class="reference-pane-graphic">
                    <img src="https://s3.amazonaws.com/cottonbureau.com/img/how-it-works/how_it_works.svg" loading="lazy" />
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <p>We’re here for you every step of the way.</p>
                  </div>
                  <div class="reference-pane-text">
                    <p>Whether you’re looking for the convenience of on demand, the quality of screen printing, or the flexibility a pre-order campaign, we’re your one-stop ecommerce shop.</p>
                    <p>From creating an account to configuring your first product, Cotton Bureau Custom is completely self-serve. You’ll be up and running in minutes, no approval necessary.</p>
                    <p>
                      Just like that, your product is live. Maybe the clock starts ticking on a two week pre-order campaign, or maybe you decide to go exclusively on demand. No matter which sales
                      method you use, you can count on Cotton Bureau to handle everything from production to taxes to replacements and exchanges.
                    </p>
                    <p>
                      When it’s time to start production, you can rely on our tested network of local, national, and international partners. We’ve been using the same trusted team for the last 10+
                      years to print, embroider, cast, forge, decorate, and otherwise manufacture exactly what you need and when you need it.
                    </p>
                    <div class="reference-pane-actions">
                      <a class="reference-pane-action-link" href="#create-product-top" v-scroll-to="'#create-product-top'">
                        Let’s Go
                        <NylonIcon icon-name="uiArrow--Right" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-3">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <h3 class="reference-pane-heading reference-pane-heading--full-border">
              <span class="reference-pane-heading-wrapper">Why should I work with Cotton Bureau Custom instead of some other print-me-a-shirt service?</span>
            </h3>
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <ul>
                      <li>The absolute finest-quality shirts and apparel</li>
                      <li>Dependable printing and production</li>
                      <li>Slick online shopping experience</li>
                      <li>Attentive customer service</li>
                      <li>The same team you already know and love 🥰</li>
                    </ul>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      Listen, there are a zillion places on the internet to get a crappy t-shirt made, but we’re not one of them. We’ve made a name for ourselves by focusing on quality at every turn:
                      comfy and stylish tees, a rock-solid print shop, a slick and accessible website experience, and quick customer service. Custom doesn’t have to mean careless.
                    </p>
                    <p>
                      If we team up on a t-shirt together, you can rest assured that it won’t be printed on low quality scratchy tees with the wrong color ink, and shipped three weeks late by people
                      you can’t reach via email. We’ve been doing this since 2013, and we’re getting better all the time. We got this.
                    </p>
                    <p>
                      Oh, by the way, all customer service is handled 100% in-house by real people — no ifs, ands, or bots. Refunds, exchanges, lost packages? Just
                      <a href="mailto:us@cottonbureau.com">email us@cottonbureau.com</a>. We’re here to help.
                    </p>
                    <div class="reference-pane-actions">
                      <a class="reference-pane-action-link" href="#create-product-top" v-scroll-to="'#create-product-top'">
                        Join the Club
                        <NylonIcon icon-name="uiArrow--Right" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-4">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <h3 class="reference-pane-heading reference-pane-heading--full-border">Couldn’t I do it myself?</h3>
                    <p>
                      Sure. We have fond memories of doing it ourselves, too. Once upon a time, before Cotton Bureau was even a twinkle in our eye, we set up a little internet t-shirt shop of our own.
                      If designing and building a store is your kind of fun, we salute you.
                    </p>
                    <p>
                      For everyone else who just wants to be up and running in minutes, that’s why Cotton Bureau exists. No need to set shipping rates or mess around with tariff codes. No 3PLs to
                      investigate, international laws to parse, or packages to track down. Say sayonara to sales tax. Enjoy global shipping at competitive rates. Sit back and relax while someone else
                      handles the customer service.
                    </p>
                    <p>Sound too good to be true? There’s only one way to find out.</p>
                    <div class="reference-pane-actions">
                      <a class="reference-pane-action-link" href="#create-product-top" v-scroll-to="'#create-product-top'">
                        Get Started Now
                        <NylonIcon icon-name="uiArrow--Right" />
                      </a>
                    </div>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-graphic">
                    <img src="https://s3.amazonaws.com/cottonbureau.com/img/how-it-works/how_it_works_factory.svg" loading="lazy" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-5">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <h3 class="reference-pane-heading reference-pane-heading--full-border">I’m sold. What do I have to do?</h3>
                  <p>&nbsp;</p>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-lede">
                    <p>We thought you’d never ask.</p>
                  </div>
                  <div class="reference-pane-text">
                    <p>
                      All you need to get started is a design. Simply upload your artwork, choose the product type, and add a title and description. That’s it, you’re live! Don’t forget to fill out
                      your profile — including payment information if this is a fundraiser for the school chess club, your organic meal delivery startup, the (extended) family choir, or that big
                      cross-country road trip. It only takes a minute.
                    </p>
                    <div class="reference-pane-actions">
                      <a class="reference-pane-action-link" href="#create-product-top" v-scroll-to="'#create-product-top'">
                        Don’t Wait Any Longer
                        <NylonIcon icon-name="uiArrow--Right" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>

      <figure class="reference-pane" id="reference-pane-6">
        <div class="reference-pane-wrapper">
          <div class="reference-pane-content reference-pane-content--split">
            <h3 class="reference-pane-heading">How much money will I make?</h3>
            <div class="reference-pane-container reference-pane-chart-container">
              <div class="reference-pane-chart">
                <img src="https://s3.amazonaws.com/cottonbureau.com/img/how-it-works/how_it_works_chart.svg" loading="lazy" />
              </div>
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-lede reference-pane-lede-small">
                    <p>Whether you just want to make a few extra bucks, raise money for a worthy cause, or generate actual revenue for your business, Cotton Bureau Custom can help.</p>
                  </div>
                </div>
              </div>
            </div>
            <div class="reference-pane-container">
              <div class="reference-pane-row">
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      There’s literally no limit to how much money you can make when you work with Cotton Bureau. Will you see seven figures in profit? Probably not, but if you do, it wouldn’t be our
                      first time.
                    </p>
                    <p>Either way, it’s 100% free to get started. All Cotton Bureau Custom accounts have access to our complete on demand catalog and extensive pre-order campaign services.</p>
                    <p>When you’re ready, just pick a product, add your profit margin, and tell people about it. It’s that simple.</p>
                    <p>Once a month as long as you’ve earned at least $25 Cotton Bureau sends you money via PayPal or direct bank transfer.</p>
                    <p>Not a bad deal for a couple of minutes setting up an account and telling a few friends.</p>
                  </div>
                </div>
                <div class="reference-pane-column">
                  <div class="reference-pane-text">
                    <p>
                      Every time you sell an on demand shirt, hat, phone case, tote, tumbler, etc. you make money. If you sell enough, we start handing out bonuses like candy. First an extra $1 for
                      each sale, then an extra $2, and it doesn’t stop there. The more you sell, the better for us all, you know?
                    </p>
                    <p>
                      If you decided to run a pre-order campaigns, it works the same way, except there’s a 12 shirt minimum before anything gets made (and you get paid). The bar to clear is a little
                      higher than on demand, but the payoff is a shirt that will last a lifetime.
                    </p>
                    <div class="reference-pane-actions">
                      <a class="reference-pane-action-link" href="#create-product-top" v-scroll-to="'#create-product-top'">
                        Let’s Get Started
                        <NylonIcon icon-name="uiArrow--Right" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </figure>
    </div>
  </section>

  <a class="hiw-submit" href="#create-product-top" v-scroll-to="'#create-product-top'">
    <span class="u-hideOnMobile">You’ve seen enough, t</span><span class="u-hideAboveMobile">T</span>ime to get started.
    <NylonIcon icon-name="uiArrow--Right" />
  </a>
</template>

<script>
import StorefrontLayout from "@/layout/storefront-layout.vue";
import "@styles/sell/how-it-works.scss";

import CreateProduct from "@/storefront/create-a-product.vue";
export default {
  name: "Custom",
  layout: StorefrontLayout,
  components: { CreateProduct },
  props: {
    createFromExistingArtwork: { type: Object, default: null },
    relaunchCampaign: { type: Object, default: null },
    initialUser: { type: Object, default: null },
    pricingTables: { type: Object, required: true },
  },
};
</script>
